import { CalendarIcon, ChevronDownIcon, Loader2 } from "lucide-react";
import { Button } from "./button";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";
import { cn } from "../lib/utils";
import { useCallback, useState, type ComponentProps, type ReactNode } from "react";

type CalendarProps = ComponentProps<typeof Calendar>;
type ButtonProps = ComponentProps<typeof Button>;

type DatePickerProps = {
  label?: ReactNode;
  value?: Date;
  onChange: (date?: Date) => void;
  required?: boolean;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;

  buttonProps?: Omit<ButtonProps, "children" | "onClick">;
  calendarProps?: Omit<CalendarProps, "mode" | "selected" | "onSelect">;
};

const DatePicker = ({
  label = "Date",
  value,
  onChange,
  required,
  placeholder = "Pick a date",
  loading = false,
  disabled = false,
  buttonProps = {},
  calendarProps = {},
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);
  const isDisabled = disabled || loading || buttonProps?.disabled;

  const handleDateSelect = useCallback(
    (date: Date | undefined) => {
      if (isDisabled) return;
      if (!date && value) {
        setOpen(false);
        return;
      }
      onChange(date);
      setOpen(false);
    },
    [onChange, isDisabled, value],
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="w-full space-y-2">
      <Label>
        {label} {required && <span className="text-destructive text-xl">*</span>}
      </Label>

      <Popover open={open} onOpenChange={(next) => !isDisabled && setOpen(next)}>
        <PopoverTrigger asChild>
          <Button
            variant={buttonProps.variant ?? "ghost"}
            disabled={isDisabled}
            className={cn(
              "w-full justify-between font-normal border",
              !value && "text-muted-foreground",
              buttonProps.className,
            )}
            {...buttonProps}
          >
            <span className="flex items-center gap-2">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {value ? value.toLocaleDateString() : placeholder}
            </span>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin opacity-70" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 opacity-60" />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0 text-xs"
          align="start"
        >
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleDateSelect}
            className={cn(
              "w-(--radix-popover-trigger-width)",
              calendarProps.className,
            )}
            {...calendarProps}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DatePicker;
